import React, { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";

const DISMISS_KEY = "hns_growlist_dismissed_at";
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const isDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < COOLDOWN_MS;
  } catch { return false; }
};

const markDismissed = () => {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* */ }
};

export const GrowTheListPopup = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [website, setWebsite] = useState(""); // honeypot

  // Only mount on public routes (not /admin)
  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) return;
    if (isDismissed()) return;

    let openedByScroll = false;
    let openedByExit = false;
    const trigger = (reason) => {
      if (openedByScroll || openedByExit) return;
      if (reason === "scroll") openedByScroll = true;
      if (reason === "exit") openedByExit = true;
      setOpen(true);
    };

    const scrollTimer = setTimeout(() => trigger("scroll"), 12000);
    const onMouseOut = (e) => {
      // fires when the pointer leaves the viewport upwards (exit intent)
      if (!e.relatedTarget && e.clientY <= 0) trigger("exit");
    };
    document.addEventListener("mouseout", onMouseOut);

    return () => {
      clearTimeout(scrollTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  const close = useCallback(() => {
    markDismissed();
    setOpen(false);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && open) close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const submit = async (e) => {
    e.preventDefault();
    if (website) { close(); return; } // bot honeypot
    if (!email) return toast.error("Enter your email");
    setBusy(true);
    try {
      await publicApi.post("/public/subscribe", {
        email, first_name: firstName, interest: "New music", consent: true,
      });
      setDone(true);
      markDismissed();
      setTimeout(() => setOpen(false), 2200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,.65)", backdropFilter: "blur(8px)" }}
         data-testid="growlist-modal">
      <div className="relative w-full max-w-[440px]"
           style={{ background: "var(--ink-raised)", border: "1px solid var(--gold)", borderRadius: "var(--radius)",
                    boxShadow: "var(--shadow-warm)", overflow: "hidden" }}>
        <button onClick={close}
                className="absolute top-3 right-3 p-2"
                style={{ background: "none", border: "none", color: "var(--ash)", cursor: "pointer" }}
                aria-label="Close" data-testid="growlist-close">
          <X size={18} />
        </button>

        <div className="p-8">
          <div className="font-caps mb-3" style={{ color: "var(--gold)", fontSize: ".62rem", letterSpacing: ".28em" }}>
            First listen · First dibs
          </div>
          <h2 className="font-display mb-3" style={{ color: "var(--parchment)", fontSize: "1.8rem", lineHeight: 1.15 }}>
            Get new music before the world does.
          </h2>
          <p className="mb-6" style={{ color: "var(--ash)", fontSize: ".88rem", lineHeight: 1.55 }}>
            Join the Heart N Soul list — release-day drops, unreleased demos, and a heads-up when P. Frazy plays your city.
          </p>

          {done ? (
            <p className="font-display py-4 text-center" style={{ color: "var(--gold)", fontSize: "1.2rem" }} data-testid="growlist-done">
              You&rsquo;re on the list.
            </p>
          ) : (
            <form onSubmit={submit} className="grid gap-3" data-testid="growlist-form">
              <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)}
                     className="hidden" aria-hidden="true" />
              <input placeholder="First name (optional)" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                     className="px-3 py-2.5" data-testid="growlist-name"
                     style={{ background: "transparent", border: "var(--rule)", borderRadius: "var(--radius)", color: "var(--parchment)" }} />
              <input type="email" required placeholder="you@yourinbox.com" value={email} onChange={(e) => setEmail(e.target.value)}
                     className="px-3 py-2.5" data-testid="growlist-email"
                     style={{ background: "transparent", border: "var(--rule)", borderRadius: "var(--radius)", color: "var(--parchment)" }} />
              <button disabled={busy} className="btn-gold justify-center mt-1" data-testid="growlist-submit">
                {busy ? "Joining…" : "Join the list"}
              </button>
              <p className="text-center" style={{ color: "var(--ash)", fontSize: ".68rem" }}>
                No spam. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default GrowTheListPopup;
